import { useState, useEffect } from 'react'
import api from '../../utils/api'
import AdminLayout from '../../components/admin/AdminLayout'
import './Admin.css'
import * as XLSX from 'xlsx'

interface Completion {
  id: number
  enrollment_id: number
  user_name: string
  organization: string
  course_title: string
  instructor: string
  credits: number
  score: number
  grade: string
  year: number
  season: string
  completed_at: string
}

export default function AdminCompletions() {
  const [completions, setCompletions] = useState<Completion[]>([])
  const [semesters, setSemesters] = useState<any[]>([])
  const [courses, setCourses] = useState<any[]>([])
  const [allCourses, setAllCourses] = useState<any[]>([])
  const [filterSemester, setFilterSemester] = useState<string>('')
  const [filterCourse, setFilterCourse] = useState<string>('')
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    loadSemesters()
    loadCourses()
    loadCompletions()
  }, [])

  useEffect(() => {
    loadCompletions()
  }, [filterSemester, filterCourse])

  useEffect(() => {
    // 학기 선택 시 해당 학기의 강의 목록 필터링
    if (filterSemester) {
      const filtered = allCourses.filter(c => c.semester_id === parseInt(filterSemester))
      setCourses(filtered)
      // 학기가 변경되면 강의 선택 초기화
      if (filterCourse) {
        const courseExists = filtered.find(c => c.id === parseInt(filterCourse))
        if (!courseExists) {
          setFilterCourse('')
        }
      }
    } else {
      // 학기 미선택 시 전체 강의 표시
      setCourses(allCourses)
    }
  }, [filterSemester, allCourses])

  const loadSemesters = async () => {
    try {
      const res = await api.get('/semesters')
      setSemesters(res.data)
    } catch (err) {
      console.error('학기 로드 오류:', err)
    }
  }

  const loadCourses = async () => {
    try {
      const res = await api.get('/admin/courses')
      setAllCourses(res.data)
    } catch (err) {
      console.error('강의 로드 오류:', err)
    }
  }

  const loadCompletions = async () => {
    try {
      setLoading(true)
      const params = new URLSearchParams()
      if (filterSemester) {
        params.append('semesterId', filterSemester)
      }
      if (filterCourse) {
        params.append('courseId', filterCourse)
      }
      
      const res = await api.get(`/admin/completions?${params.toString()}`)
      setCompletions(res.data)
    } catch (err) {
      console.error('이수 내역 로드 오류:', err)
    } finally {
      setLoading(false)
    }
  }

  const getTotalCredits = () => {
    return completions.reduce((sum, c) => sum + c.credits, 0)
  }

  const exportToExcel = () => {
    if (completions.length === 0) {
      alert('반출할 데이터가 없습니다.')
      return
    }

    // 엑셀 데이터 형식 변환
    const excelData = completions.map((c, idx) => ({
      '번호': idx + 1,
      '학생명': c.user_name,
      '소속기관': c.organization || '-',
      '학년도/계절': `${c.year}년 ${c.season}`,
      '과목명': c.course_title,
      '교수명': c.instructor,
      '이수학점': c.credits,
      '점수': c.score,
      '성적': c.grade,
      '이수일자': new Date(c.completed_at).toLocaleDateString('ko-KR')
    }))

    // 합계 행 추가
    excelData.push({
      '번호': '',
      '학생명': '',
      '소속기관': '',
      '학년도/계절': '',
      '과목명': '',
      '교수명': '합계',
      '이수학점': getTotalCredits(),
      '점수': '',
      '성적': '',
      '이수일자': ''
    } as any)

    // 워크북 생성
    const wb = XLSX.utils.book_new()
    const ws = XLSX.utils.json_to_sheet(excelData)

    // 컬럼 너비 설정
    ws['!cols'] = [
      { wch: 6 },  // 번호
      { wch: 10 }, // 학생명
      { wch: 15 }, // 소속기관
      { wch: 12 }, // 학년도/계절
      { wch: 20 }, // 과목명
      { wch: 10 }, // 교수명
      { wch: 10 }, // 이수학점
      { wch: 8 },  // 점수
      { wch: 8 },  // 성적
      { wch: 12 }  // 이수일자
    ]

    XLSX.utils.book_append_sheet(wb, ws, '이수내역')

    // 파일명 생성
    const selectedSemester = semesters.find(s => s.id === parseInt(filterSemester))
    const selectedCourse = courses.find(c => c.id === parseInt(filterCourse))
    
    let fileName = '이수내역'
    if (selectedSemester) {
      fileName += `_${selectedSemester.year}년_${selectedSemester.season}`
    }
    if (selectedCourse) {
      fileName += `_${selectedCourse.title}`
    }
    if (!selectedSemester && !selectedCourse) {
      fileName += '_전체'
    }
    fileName += '.xlsx'

    // 파일 다운로드
    XLSX.writeFile(wb, fileName)
  }

  if (loading) {
    return (
      <AdminLayout>
        <div className="loading">로딩 중...</div>
      </AdminLayout>
    )
  }

  return (
    <AdminLayout>
      <div className="admin-page">
        <h1>이수 관리</h1>
        
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <div>
            <p style={{ margin: 0, color: '#666' }}>
              총 <strong>{completions.length}</strong>건의 이수 완료 내역이 있습니다.
              {completions.length > 0 && (
                <span style={{ marginLeft: '15px', color: '#2563eb', fontWeight: 'bold' }}>
                  총 이수학점: {getTotalCredits()}학점
                </span>
              )}
            </p>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', flexWrap: 'wrap' }}>
            <button 
              onClick={exportToExcel}
              className="btn btn-success"
              disabled={completions.length === 0}
              style={{ padding: '0.5rem 1rem', whiteSpace: 'nowrap' }}
            >
              📊 엑셀 반출
            </button>
            <label style={{ fontWeight: 500 }}>학기:</label>
            <select 
              value={filterSemester} 
              onChange={(e) => setFilterSemester(e.target.value)}
              style={{ padding: '0.5rem', borderRadius: '6px', border: '1px solid #ddd', minWidth: '150px' }}
            >
              <option value="">전체</option>
              {semesters.map(s => (
                <option key={s.id} value={s.id}>{s.year}년 {s.season}</option>
              ))}
            </select>
            <label style={{ fontWeight: 500 }}>강의:</label>
            <select 
              value={filterCourse} 
              onChange={(e) => setFilterCourse(e.target.value)}
              style={{ 
                padding: '0.5rem', 
                borderRadius: '6px', 
                border: '1px solid #ddd', 
                minWidth: '200px'
              }}
            >
              <option value="">전체</option>
              {courses.map(c => (
                <option key={c.id} value={c.id}>
                  {c.title} ({c.instructor}) {c.year && c.season ? `- ${c.year}년 ${c.season}` : ''}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="admin-list">
          {completions.length === 0 ? (
            <div style={{ 
              textAlign: 'center', 
              padding: '60px 20px', 
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '2px dashed #dee2e6'
            }}>
              <p style={{ fontSize: '1.2rem', color: '#666', marginBottom: '0.5rem' }}>
                ✅ 이수 완료 내역이 없습니다
              </p>
              <p style={{ color: '#999', margin: 0 }}>
                {filterSemester ? '선택한 학기의 이수 완료 내역이 없습니다.' : '등록된 이수 완료 내역이 없습니다.'}
              </p>
            </div>
          ) : (
            <div style={{ overflowX: 'auto' }}>
              <table>
                <thead>
                  <tr>
                    <th>번호</th>
                    <th>학생명</th>
                    <th>소속기관</th>
                    <th>학년도/계절</th>
                    <th>과목명</th>
                    <th>교수명</th>
                    <th>이수학점</th>
                    <th>점수</th>
                    <th>성적</th>
                    <th>이수일자</th>
                  </tr>
                </thead>
                <tbody>
                  {completions.map((c, idx) => (
                    <tr key={c.id}>
                      <td>{idx + 1}</td>
                      <td><strong>{c.user_name}</strong></td>
                      <td>{c.organization || '-'}</td>
                      <td style={{ whiteSpace: 'nowrap' }}>{c.year}년 {c.season}</td>
                      <td>{c.course_title}</td>
                      <td>{c.instructor}</td>
                      <td style={{ textAlign: 'center', fontWeight: 'bold', color: '#2563eb' }}>
                        {c.credits}학점
                      </td>
                      <td style={{ textAlign: 'center' }}>{c.score}</td>
                      <td style={{ textAlign: 'center' }}>
                        <span style={{ 
                          padding: '4px 8px', 
                          borderRadius: '4px', 
                          background: '#d4edda', 
                          color: '#155724',
                          fontWeight: 'bold'
                        }}>
                          {c.grade}
                        </span>
                      </td>
                      <td style={{ whiteSpace: 'nowrap' }}>
                        {new Date(c.completed_at).toLocaleDateString('ko-KR')}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr style={{ background: '#f8f9fa', fontWeight: 'bold' }}>
                    <td colSpan={6} style={{ textAlign: 'right', padding: '12px' }}>
                      합계
                    </td>
                    <td style={{ textAlign: 'center', color: '#2563eb', fontSize: '1.1em' }}>
                      {getTotalCredits()}학점
                    </td>
                    <td colSpan={3}></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>
    </AdminLayout>
  )
}

